import { observable, action, autorun, computed } from 'mobx'
import { setter }                                from 'mobx-decorators'
import { orderBy }                               from 'lodash/fp'

import xhr                                       from 'helpers/XHR'
import uiStore                                   from 'stores/UiStore'
import getError                                  from 'helpers/ErrorParser'

import Pagination                                from 'stores/models/Pagination'

class StudentStore {
  //status
  @setter @observable isLoading = false
  @setter @observable isFetchingStudents = false
  @setter @observable isFetchingContacts = false
  @setter @observable isSearchingStudents = false
  @setter @observable isError = null
  @observable pagination = new Pagination(this)

  //Students
  @observable students = observable.map()
  @observable searchedStudents = observable.map()
  @setter @observable currentStudent = null

  //Contacts
  @observable contacts = observable.map()

  constructor() {
    this.initAutoruns()
  }

  initAutoruns = () => {
    this.autoErrorNotifier()
  }

  autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    'error'
        })
      }
    })
  }

  @computed
  get studentParams() {
    return {
      page:      this.pagination.current,
      limit:     this.pagination.pageSize,
      direction: 'asc',
      sort:      'last_name',
      only:      [
        'avatar_url',
        'full_name',
        'first_name',
        'last_name',
        'id',
        'grade',
        'school.school_name',
        'secondary_school.school_name',
        'tertiary_school.school_name',
        'phone_type'
      ].join(',')
    }
  }

  @computed
  get descStudents() {
    return orderBy((s) => s.last_name, 'asc')(
      this.students.values()
    )
  }

  @computed
  get messageEnabledContacts() {
    return this.contacts
      .values()
      .filter((c) => c.phone_type !== 'landline')
  }

  @action clearData = () => {
    this.students.clear()
  }

  @computed
  get isLoadingMoreStudents() {
    return (
      this.isFetchingStudents &&
      !this.searchedStudents.size &&
      this.students.size
    )
  }

  @action setPagination = ({ total }) => {
    this.pagination.setTotal(parseInt(total))
    this.pagination.calculateTotal
  }

  @action fetchAllStudents = async() => {
    const params = {
      limit: 1000,
      only:  ['id', 'first_name', 'last_name', 'grade'].join(',')
    }

    try {
      this.setIsLoading(true)
      this.setIsError(false)
      const res = await xhr.get(`/students`, { params })

      this.fetchAllStudentsOk(res)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsLoading(false)
    }
  }

  @action
  fetchAllStudentsOk = ({ headers, data }) => {
    this.setPagination(headers)
    data.forEach(this.createStudent)
  }

  @action
  createStudent = (student) => {
    if (this.students.has(student.id)) return
    this.students.set(student.id, student)
  }

  @action
  fetchStudents = async() => {
    try {
      this.pagination.clear()
      this.setIsFetchingStudents(true)
      this.searchedStudents.clear()
      this.students.clear()

      const res = await xhr.get('/students', {
        params: this.studentParams
      })

      this.fetchStudentsOk(res)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingStudents(false)
    }
  }

  @action
  fetchStudentsOk = ({ data, headers }) => {
    this.setPagination(headers)
    data.forEach((s) => {
      this.students.set(s.id, s)
    })
  }

  @action
  fetchMoreStudents = async() => {
    try {
      this.searchedStudents.clear()
      this.setIsFetchingStudents(true)
      const res = await xhr.get('students', {
        params: this.studentParams
      })

      this.fetchMoreStudentsOk(res)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingStudents(false)
    }
  }

  @action
  fetchMoreStudentsOk = ({ data }) => {
    this.pagination.setCurrent(this.pagination.current + 1)

    data.forEach((s) => {
      this.students.set(s.id, s)
    })
  }

  @action
  searchStudents = async(text) => {
    try {
      this.setIsSearchingStudents(true)
      const { data } = await xhr.get('typeahead/students', {
        params: {
          text_filter: text
        }
      })

      this.searchStudentsOk(data)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsSearchingStudents(false)
    }
  }

  @action
  searchStudentsOk = (students) => {
    this.searchedStudents.clear()
    students.forEach((s) => {
      this.searchedStudents.set(s.id, s)
    })
  }

  @action
  fetchContacts = async(hasPhone = false) => {
    if (!this.currentStudent) return

    try {
      this.contacts.clear()
      this.setIsFetchingContacts(true)
      const { data } = await xhr.get(
        `students/${this.currentStudent.id}/contacts`,
        {
          params: {
            id:   this.currentStudent.id,
            only: [
              'id',
              'name',
              'primary',
              'relationship',
              'student.full_name',
              'student.id',
              'phone',
              'phone_type',
              'preferred_language'
            ].join(','),
            include_contact_phone_type: 'true',
            has_phone: hasPhone,
          }
        }
      )

      data.forEach((c) => {
        this.contacts.set(c.id, c)
      })
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingContacts(false)
    }
  }
}

export default new StudentStore()
